import { useMemo } from "react";
import { useSelector } from "react-redux";
import { returnFound } from "find-and";
import { ServiceInfoDc } from "@evergis/api";

import { getProjectLayers } from "../../../ducks/project/selectors";
import { getTempSourceLayers } from "../../../ducks/tempSourceLayers/selectors";
import { getLayerInfo } from "../../../evergis/core/layer/getLayerInfo";
import { combineLayers, useProjectLayerInfos, useProjectLayers } from "../../../hooks/project/useProjectLayers";
import { useWidgetConfiguration } from "../../Dashboard/hooks/useWidgetConfiguration";
import { useIsDashboard } from "../../../hooks/location/useIsDashboard";

import { WidgetType } from "../../FeatureCard/types";

export interface GeometrySelectionActiveLayer {
  name: string;
  alias?: string;
  isVisible?: boolean;
  objectCount?: number;
  condition?: string;
  type: string;
}

export const useTableActiveLayers = (): GeometrySelectionActiveLayer[] => {
  const currentProjectLayers = useSelector(getProjectLayers);
  const projectLayers = useProjectLayers();
  const { layerInfos } = useProjectLayerInfos();
  const tempSourceLayers = useSelector(getTempSourceLayers);
  const dashboardWidgetConfig = useWidgetConfiguration(WidgetType.Dashboard);
  const dashboardLayers = useMemo(() => dashboardWidgetConfig?.currentConfig?.layers ?? [], [dashboardWidgetConfig]);
  const isDashboard = useIsDashboard();

  const processedLayers = useMemo(() => {
    const enrichedDashboardLayers = dashboardLayers.map(item => ({
      ...item,
      isVisible: returnFound(projectLayers, { name: item.name })?.isVisible,
    }));

    return combineLayers(isDashboard ? enrichedDashboardLayers : projectLayers)
      .filter(Boolean)
      .filter(layer => !["RemoteTileService"].includes(currentProjectLayers[layer.name]?.type));
  }, [currentProjectLayers, dashboardLayers, isDashboard, projectLayers]);

  const allLayers = useMemo(
    () => [...tempSourceLayers.map(layer => getLayerInfo(layer)), ...processedLayers],
    [tempSourceLayers, processedLayers],
  );

  return useMemo(() => {
    return allLayers.map(processedLayer => {
      const { alias, isVisible, name } = processedLayer || {};
      const extraLayerInfo = layerInfos?.find(layer => layer.name === name) as ServiceInfoDc;

      return {
        name,
        alias,
        isVisible,
        objectCount: extraLayerInfo?.objectCount,
        condition: extraLayerInfo?.condition,
        type: extraLayerInfo?.type,
      };
    });
  }, [allLayers, layerInfos]);
};
